import React, { FunctionComponent, JSX, useState } from "react";
import { Story } from "../../../../types";
import { constants } from "../../../../constants";
import { BoxyNumbers } from "../../../common/boxy-numbers/boxy-numbers";

type Props = {
  selected: Map<number, boolean>;
  setSelected: (id: number, selected: boolean) => void;
  partNumber: number;
  story?: Story;
};

type PPP = {
  id: string;
  label: string | JSX.Element;
  setSelected: (selected: boolean) => void;
  selected?: boolean;
  disabled?: boolean;
};

const emojis = constants.emojis; //["🙏", "🌶️", "👀", "🧠", "💩", "🤪", "❤️", "🔥", "😂", "⭐"]

const BBB: FunctionComponent<PPP> = ({
  id,
  label,
  setSelected,
  selected,
  disabled,
}) => {
  const [toggled, setToggled] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      setSelected(true);
      setToggled(true);
      // Reset toggled after animation completes (500ms)
      setTimeout(() => setToggled(false), 500);
    } else {
      setSelected(false);
      setToggled(false);
    }
  }

  // Define filter classes based on state
  const getFilterClass = () => {
    if (selected) {
      return "contrast-100 brightness-100 saturate-100";
    } else if (isHovered) {
      return "grayscale-50 contrast-100 brightness-75";
    } else {
      return "grayscale contrast-100 brightness-75";
    }
  };

  const getLabelClass = () => {
    const baseClass =
      "relative cursor-pointer border border-transparent flex text-center justify-center items-center select-none z-10 self-stretch p-1 filter peer-focus-visible:outline peer-focus-visible:outline-auto peer-focus-visible:outline-black";
    const colorClass = label === "❤️" ? "text-red-600" : "";
    const filterClass = getFilterClass();

    return `${baseClass} ${colorClass} ${filterClass}`.trim();
  };

  return (
    <>
      <input
        className="peer opacity-0 h-0 w-0 m-0"
        disabled={disabled}
        onChange={handleChange}
        id={id}
        checked={selected ?? false}
        type="checkbox"
      />
      <label
        htmlFor={id}
        className={getLabelClass()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {label}
        <span
          className={`absolute -z-10 ${toggled ? "animate-vote-fancy" : "opacity-0"}`}
        >
          {label}
        </span>
      </label>
    </>
  );
};

const VoteRow = ({ partNumber, selected, setSelected }: Props) => {
  // Part 1 VOTE
  return (
    <div className="text-base flex justify-center items-center text-center uppercase pb-1">
      <div className="italic text-base">Part {partNumber} REACTS:</div>
      {emojis.map((v, idx) => (
        <BBB
          key={"bbb-" + idx}
          selected={selected.get(idx)}
          setSelected={(s) => setSelected(idx, s)}
          id={partNumber + "-" + idx}
          label={v}
        />
      ))}
      ― <span style={{ alignSelf: "stretch" }} />{" "}
      <BoxyNumbers
        value={
          selected.size > 0
            ? Array.from(selected.values())
                .map((v) => (v ? (100 as number) : (0 as number)))
                .reduce((prev, cur) => prev + cur)
            : 0
        }
        length={3}
      />
    </div>
  );
};

export default VoteRow;
